import React, { useState, useEffect } from 'react';
import toastr from 'toastr';
import { getMSPCompanies, createCompany, updateCompany, updateCompanyAccounts, getCompanyDashboard } from '../utils/companyApi';

const MSPCompanies = ({ mspConfig, tenants, isSidebarOpen }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [newCompany, setNewCompany] = useState({ companyName: '', domain: '', rootUserEmail: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCompany, setEditCompany] = useState({ companyID: '', companyName: '', domain: '', rootUserEmail: '' });
  const [newUser, setNewUser] = useState({ email: '', accessLevel: 'member' });

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      console.log('Loading companies for mspConfig:', mspConfig);
      if (!mspConfig?.mspID) {
        console.error('No mspID found in mspConfig:', mspConfig);
        toastr.error('MSP configuration not loaded');
        setLoading(false);
        return;
      }
      const data = await getMSPCompanies(mspConfig.mspID);
      console.log('Companies data received:', data);
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error loading companies:', error);
      toastr.error('Failed to load companies: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.companyName) {
      toastr.error('Company name is required');
      return;
    }
    if (!newCompany.rootUserEmail) {
      toastr.error('Root user email is required');
      return;
    }

    try {
      await createCompany(mspConfig.mspID, newCompany.companyName, newCompany.domain, newCompany.rootUserEmail);
      toastr.success('Company created successfully');
      setShowCreateModal(false);
      setNewCompany({ companyName: '', domain: '', rootUserEmail: '' });
      loadCompanies();
    } catch (error) {
      toastr.error('Failed to create company');
    }
  };

  const handleManageAccounts = (company) => {
    setSelectedCompany(company);
    
    // Get current available accounts
    const availableAccounts = getAllMSPAccounts();
    
    // Filter company accounts to only include ones that still exist in available accounts
    const validSelectedAccounts = (company.accounts || []).filter(companyAccount => 
      availableAccounts.some(available => available.accountId === companyAccount.accountId)
    );
    
    setSelectedAccounts(validSelectedAccounts);
    setShowAccountModal(true);
  };

  const handleEditCompany = (company) => {
    const rootUser = (company.users || []).find(u => u.accessLevel === 'admin');
    setEditCompany({
      companyID: company.companyID,
      companyName: company.companyName,
      domain: company.domain || '',
      rootUserEmail: rootUser?.email || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCompany = async () => {
    if (!editCompany.companyName) {
      toastr.error('Company name is required');
      return;
    }
    if (!editCompany.rootUserEmail) {
      toastr.error('Root user email is required');
      return;
    }

    try {
      await updateCompany(mspConfig.mspID, editCompany.companyID, editCompany.companyName, editCompany.domain, editCompany.rootUserEmail);
      toastr.success('Company updated successfully');
      setShowEditModal(false);
      loadCompanies();
    } catch (error) {
      toastr.error('Failed to update company: ' + error.message);
    }
  };

  const handleSaveAccounts = async () => {
    try {
      await updateCompanyAccounts(mspConfig.mspID, selectedCompany.companyID, selectedAccounts);
      toastr.success('Company accounts updated successfully');
      setShowAccountModal(false);
      loadCompanies();
    } catch (error) {
      toastr.error('Failed to update company accounts');
    }
  };

  const getAllMSPAccounts = () => {
    const allAccounts = [];
    (tenants || []).forEach(tenant => {
      const cloudAccounts = tenant.cloudAccounts;
      if (!cloudAccounts) return;
      
      const accounts = typeof cloudAccounts === 'string' ? JSON.parse(cloudAccounts) : cloudAccounts;
      
      // Add standalone accounts
      (accounts.aws || []).forEach(acc => {
        allAccounts.push({
          tenantID: tenant.tenantID,
          accountId: acc.accountId,
          accountName: acc.accountName || acc.alias || acc.accountId
        });
      });
      
      // Add organization accounts
      (accounts.organizations || []).forEach(org => {
        // Management account
        allAccounts.push({
          tenantID: tenant.tenantID,
          accountId: org.id,  // Use org.id as management account ID
          accountName: `${org.alias || 'Management Account'} (Management)`
        });
        
        // Member accounts
        (org.memberAccounts || []).filter(m => m.connected).forEach(member => {
          allAccounts.push({
            tenantID: tenant.tenantID,
            accountId: member.accountId,
            accountName: member.accountName || member.accountId
          });
        });
      });
    });
    
    return allAccounts;
  };

  const toggleAccountSelection = (account) => {
    const isSelected = selectedAccounts.some(acc => acc.accountId === account.accountId);
    if (isSelected) {
      setSelectedAccounts(selectedAccounts.filter(acc => acc.accountId !== account.accountId));
    } else {
      setSelectedAccounts([...selectedAccounts, account]);
    }
  };

  const handleInviteUser = async () => {
    if (!newUser.email) {
      toastr.error('Email is required');
      return;
    }
    try {
      const response = await fetch(
        'https://htb4wmezrg.execute-api.us-west-2.amazonaws.com/prod/v2/company/users/invite',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyID: selectedCompany.companyID,
            email: newUser.email,
            accessLevel: newUser.accessLevel || 'member'
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to invite user');
      }
      
      toastr.success('User invited successfully');
      setShowUserModal(false);
      setNewUser({ email: '', accessLevel: 'member' });
      loadCompanies();
    } catch (error) {
      toastr.error('Failed to invite user: ' + error.message);
    }
  };

  const getTenantName = (tenantID) => {
    const tenant = tenants.find(t => t.tenantID === tenantID);
    return tenant ? (tenant.companyName || tenant.email.split('@')[0]) : tenantID;
  };

  const getAccountCount = (tenantID) => {
    const tenant = tenants.find(t => t.tenantID === tenantID);
    if (!tenant) return 0;
    const cloudAccounts = tenant.cloudAccounts || {};
    const standaloneCount = (cloudAccounts.aws || []).length;
    const orgCount = (cloudAccounts.organizations || []).reduce((sum, org) => {
      return sum + 1 + (org.memberAccounts || []).filter(m => m.connected).length;
    }, 0);
    return standaloneCount + orgCount;
  };

  if (loading) {
    return (
      <div className="container-fluid p-4">
        <h2>Companies</h2>
        <div className="d-flex justify-content-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  console.log('Rendering companies:', companies);

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Companies</h2>
        <button className="btn" style={{ backgroundColor: '#342EAD', color: 'white', border: 'none' }} onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus me-2"></i>Create Company
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Domain</th>
                  <th>Assigned Accounts</th>
                  <th>Users</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(companies || []).map(company => (
                  <tr key={company.companyID}>
                    <td><strong>{company.companyName}</strong></td>
                    <td>{company.domain || <span className="text-muted">-</span>}</td>
                    <td>
                      <span className="badge bg-info">
                        {(company.accounts || []).length}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-secondary">
                        {(company.users || []).length}
                      </span>
                    </td>
                    <td>{new Date(company.createdAt).toLocaleDateString()}</td>
                    <td>
                      <button 
                        className="btn btn-sm btn-outline-info me-2"
                        onClick={() => handleEditCompany(company)}
                      >
                        <i className="fas fa-edit me-1"></i>Edit
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => {
                          setSelectedCompany(company);
                          setShowUserModal(true);
                        }}
                      >
                        Add User
                      </button>
                      <button 
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleManageAccounts(company)}
                      >
                        Manage Accounts
                      </button>
                    </td>
                  </tr>
                ))}}
              </tbody>
            </table>
            {companies.length === 0 && (
              <div className="text-center py-4">
                <p className="text-muted">No companies created yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Company Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Company</h5>
                <button className="btn-close" onClick={() => setShowEditModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Company Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={editCompany.companyName}
                    onChange={(e) => setEditCompany({...editCompany, companyName: e.target.value})}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Domain (Optional)</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={editCompany.domain}
                    onChange={(e) => setEditCompany({...editCompany, domain: e.target.value})}
                    placeholder="company.com"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Root User Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={editCompany.rootUserEmail}
                    onChange={(e) => setEditCompany({...editCompany, rootUserEmail: e.target.value})}
                    placeholder="admin@company.com"
                    required
                  />
                  <div className="form-text">This user will be the company admin</div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button className="btn" style={{ backgroundColor: '#342EAD', color: 'white', border: 'none' }} onClick={handleUpdateCompany}>Update</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Create Company</h5>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Company Name *</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newCompany.companyName}
                    onChange={(e) => setNewCompany({...newCompany, companyName: e.target.value})}
                    placeholder="Enter company name"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Domain (Optional)</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={newCompany.domain}
                    onChange={(e) => setNewCompany({...newCompany, domain: e.target.value})}
                    placeholder="company.com"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Root User Email *</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={newCompany.rootUserEmail}
                    onChange={(e) => setNewCompany({...newCompany, rootUserEmail: e.target.value})}
                    placeholder="admin@company.com"
                    required
                  />
                  <div className="form-text">This user will be created as company admin and can manage the company</div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                <button className="btn" style={{ backgroundColor: '#342EAD', color: 'white', border: 'none' }} onClick={handleCreateCompany}>Create</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Accounts Modal */}
      {showAccountModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Manage Accounts - {selectedCompany?.companyName}</h5>
                <button className="btn-close" onClick={() => setShowAccountModal(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">Select accounts to assign to this company:</p>
                <div className="row">
                  {getAllMSPAccounts().map(account => {
                    const isSelected = selectedAccounts.some(acc => acc.accountId === account.accountId);
                    return (
                      <div key={account.accountId} className="col-md-6 mb-2">
                        <div 
                          className={`card ${isSelected ? 'border-primary bg-light' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => toggleAccountSelection(account)}
                        >
                          <div className="card-body py-2">
                            <div className="form-check">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={() => toggleAccountSelection(account)}
                              />
                              <label className="form-check-label">
                                <strong>{account.accountName}</strong><br/>
                                <small className="text-muted">{account.accountId}</small>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {getAllMSPAccounts().length === 0 && (
                  <div className="text-center py-4">
                    <p className="text-muted">No accounts available from managed tenants</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowAccountModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSaveAccounts}>
                  Save ({selectedAccounts.length} accounts)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUserModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Invite User to {selectedCompany?.companyName}</h5>
                <button className="btn-close" onClick={() => setShowUserModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Email</label>
                  <input 
                    type="email" 
                    className="form-control" 
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Access Level</label>
                  <select 
                    className="form-select"
                    value={newUser.accessLevel}
                    onChange={(e) => setNewUser({...newUser, accessLevel: e.target.value})}
                  >
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowUserModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleInviteUser}>Invite</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MSPCompanies;